/**
 * Sync messages serveur ↔ local — Phase 4, incrément 4.8.
 *
 * **Scope 4.8 : texte uniquement**. Étend ADR 0009 aux messages
 * nested dans `home.data.messages`, avec une particularité
 * importante : **les messages avec `audioUrl` ou `attachmentUrl`
 * sont skipés** (pas migrés, pas écrasés). Ils restent locaux
 * jusqu'à la Phase 4.9 qui ajoutera l'upload R2 + un champ
 * `media_id` côté API.
 *
 * Symétrique de tasks-sync (4.7) sinon : idempotent, multi-homes
 * tolérant, déclenché par `homes-sync.ts` après que les homes
 * ont leurs UUIDs serveur.
 */
import * as messagesApi from '../api/messages.js';
import { ApiError } from '../api/client.js';
import {
  applyMessagesSyncResult,
  getHomesSnapshot,
  isServerHomeId,
  isTextOnlyMessage,
  type ServerMessagePayload,
} from './homes.js';
import type { ClassicHomeData, FamilyMessage } from '@nestor/shared-types';

export interface MessagesSyncStatus {
  state: 'idle' | 'syncing' | 'ok' | 'error';
  lastAt: number | null;
  errorCode: string | null;
}

let status: MessagesSyncStatus = { state: 'idle', lastAt: null, errorCode: null };
let inFlight: Promise<void> | null = null;

export function getMessagesSyncStatus(): MessagesSyncStatus {
  return status;
}

/**
 * Synchronise les messages texte-only pour tous les foyers UUID
 * classiques. Idempotent.
 *
 * Pour chaque home :
 *   1. POST les messages **texte-only locaux pré-auth** → idMap.
 *      Les messages avec media sont skipés (Phase 4.9).
 *   2. GET la liste serveur.
 *   3. `applyMessagesSyncResult(homeId, …)` atomique.
 *
 * Erreurs ponctuelles : warn + continue avec les autres homes.
 */
export async function syncMessagesForAllHomes(): Promise<void> {
  if (inFlight) return inFlight;
  inFlight = doSync().finally(() => {
    inFlight = null;
  });
  return inFlight;
}

async function doSync(): Promise<void> {
  status = { state: 'syncing', lastAt: status.lastAt, errorCode: null };
  const { homes } = getHomesSnapshot();

  let anyError = false;
  let lastErrorCode: string | null = null;

  for (const home of homes) {
    if (!isServerHomeId(home.id)) continue;
    if (home.mode !== 'classic') continue;

    const data = home.data as ClassicHomeData;

    // 1. Migration POST des messages pré-auth texte-only.
    //    Les messages avec audioUrl/attachmentUrl sont skipés.
    // Phase 3.3.3 : offline-first via SendResult (cf. 3.3.2).
    const idMap = new Map<string, string>();
    const migratable = data.messages.filter((m) => !isServerHomeId(m.id) && isTextOnlyMessage(m));
    for (const message of migratable) {
      try {
        const result = await messagesApi.createMessage(
          home.id,
          buildCreateInput(message),
          message.id,
        );
        if (result.kind === 'sent') {
          idMap.set(message.id, result.data.message.id);
        }
      } catch (err) {
        const code = errorCode(err);
        anyError = true;
        lastErrorCode = code;
        console.warn('[messages-sync] migration POST échouée', {
          homeId: home.id,
          messageId: message.id,
          error: code,
        });
      }
    }

    // 2. Fetch + 3. apply.
    try {
      const serverMessages = await messagesApi.listMessages(home.id);
      const payload: ServerMessagePayload[] = serverMessages.map((m) => ({
        id: m.id,
        text: m.text,
        // 4.11 : audio support — mediaKey + audioUrl ingérés au merge.
        mediaKey: m.mediaKey,
        audioUrl: m.audioUrl,
        createdAt: m.createdAt,
      }));
      applyMessagesSyncResult(home.id, { idMap, serverMessages: payload });
    } catch (err) {
      if (idMap.size > 0) {
        applyMessagesSyncResult(home.id, { idMap });
      }
      const code = errorCode(err);
      anyError = true;
      lastErrorCode = code;
      console.warn('[messages-sync] fetch /messages échoué', { homeId: home.id, error: code });
    }
  }

  status = anyError
    ? { state: 'error', lastAt: Date.now(), errorCode: lastErrorCode }
    : { state: 'ok', lastAt: Date.now(), errorCode: null };
}

function buildCreateInput(message: FamilyMessage): messagesApi.CreateMessageInput {
  // `text` est garanti truthy par le filtre `isTextOnlyMessage`
  // côté caller. Cast safe pour satisfaire le compilateur sur le
  // champ optionnel du type proto.
  return { text: message.text! };
}

function errorCode(err: unknown): string {
  if (err instanceof ApiError) {
    return err.code ?? `http_${err.status.toString()}`;
  }
  if (err instanceof Error) return err.name;
  return 'unknown';
}

/** @internal pour tests — reset le state module-level. */
export function _resetMessagesSyncForTests(): void {
  status = { state: 'idle', lastAt: null, errorCode: null };
  inFlight = null;
}
